import React, { useEffect, useMemo, useRef, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { cn } from '@/lib/utils'
import { Building2, Loader2, CheckCircle2, AlertCircle, Globe, ArrowRight, Check } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FALLBACK_BRAND } from '@/lib/brand'
import { apiFetch, apiErrorMessage } from '@/lib/csrf'

interface Property {
  shortname: string
  name: string
  currency: string
  status: string
  already_onboarded: boolean
  error?: string
}

interface PropertySelectorProps {
  credentialId: number
  properties: Property[]
  /** The business the property will be filed under — named in the confirmation. */
  accountName?: string | null
  /** Back to step 1. Lives in this component's footer so both directions sit together. */
  onBack: () => void
  onSelect: (propertyId: number, redirectTo: string) => void
}


/**
 * Step 2 — CHOOSE a property, then confirm it.
 *
 * Clicking a card selects; it does not import. The import is one-way — the POST
 * creates the Property, syncs its structure from the PMS, seeds base rates and
 * starts the account's billing clock, and there is no operator-facing undo — so
 * the commit belongs behind a deliberate second action, not behind the first
 * click on a card that reads like a menu.
 *
 * The cards are a real ARIA radiogroup (roving tabindex, arrow keys select), so
 * the "this is a choice" affordance holds for keyboard and screen readers too.
 */
export default function PropertySelector({
  credentialId,
  properties,
  accountName,
  onBack,
  onSelect,
}: PropertySelectorProps) {
  const { props } = usePage()
  const brand =
    typeof props.brand === 'string' && props.brand.trim() !== '' ? props.brand : FALLBACK_BRAND

  const [selected, setSelected] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [focusIndex, setFocusIndex] = useState(0)

  const availableProperties = useMemo(() => properties.filter((p) => !p.error), [properties])
  const errorProperties = useMemo(() => properties.filter((p) => p.error), [properties])
  // The radiogroup's options: everything a click could actually choose.
  const selectable = useMemo(
    () => availableProperties.filter((p) => !p.already_onboarded),
    [availableProperties]
  )

  const cardRefs = useRef<(HTMLButtonElement | null)[]>([])
  const confirmRef = useRef<HTMLDivElement>(null)
  const hasRevealedConfirm = useRef(false)

  const selectedProperty = selected
    ? availableProperties.find((p) => p.shortname === selected) ?? null
    : null

  // Bring the confirmation into view the FIRST time it appears — with a long list the
  // ask can otherwise land below the fold. `block: 'nearest'` is a no-op when it is
  // already visible, so a short list never jumps.
  useEffect(() => {
    if (!selected || hasRevealedConfirm.current) return
    hasRevealedConfirm.current = true
    confirmRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
  }, [selected])

  function choose(property: Property, index: number) {
    if (property.already_onboarded || property.error || submitting) return
    setSelected(property.shortname)
    setFocusIndex(index)
    setError(null)
  }

  // Standard radiogroup keyboard model: the arrows move focus AND check. Safe here
  // precisely because checking is now inert — nothing is imported until Connect.
  function moveSelection(nextIndex: number) {
    if (selectable.length === 0 || submitting) return
    const index = (nextIndex + selectable.length) % selectable.length
    setSelected(selectable[index].shortname)
    setFocusIndex(index)
    setError(null)
    cardRefs.current[index]?.focus()
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault()
        moveSelection(focusIndex + 1)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault()
        moveSelection(focusIndex - 1)
        break
      case 'Home':
        e.preventDefault()
        moveSelection(0)
        break
      case 'End':
        e.preventDefault()
        moveSelection(selectable.length - 1)
        break
    }
  }

  async function handleConfirm() {
    if (!selectedProperty || submitting) return

    setError(null)
    setSubmitting(true)

    try {
      const response = await apiFetch('/onboarding/select_property', {
        method: 'POST',
        body: JSON.stringify({
          credential_id: credentialId,
          shortname: selectedProperty.shortname,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        const messages = data.errors?.join(', ') || 'Failed to set up property. Please try again.'
        setError(messages)
        setSubmitting(false)
        return
      }

      onSelect(data.propertyId, data.redirectTo)
    } catch (e) {
      setError(apiErrorMessage(e, 'Network error. Please check your connection and try again.'))
      setSubmitting(false)
    }
  }

  return (
    <div className="animate-fade-in-up">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <Building2 className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Select a property</h2>
          <p className="text-sm text-muted-foreground">
            Choose the property you want {brand} to manage. Nothing is connected until you confirm.
          </p>
        </div>
      </div>

      {/* Property cards — an ARIA radiogroup, not a row of actions. */}
      <div
        role="radiogroup"
        aria-label="Properties available to connect"
        onKeyDown={handleKeyDown}
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
      >
        {availableProperties.map((property, index) => {
          const optionIndex = selectable.findIndex((p) => p.shortname === property.shortname)
          const isSelectable = optionIndex !== -1
          const isSelected = selected === property.shortname

          return (
            <button
              key={property.shortname}
              ref={(el) => {
                if (isSelectable) cardRefs.current[optionIndex] = el
              }}
              type="button"
              role="radio"
              aria-checked={isSelected}
              tabIndex={isSelectable && optionIndex === focusIndex ? 0 : -1}
              onClick={() => choose(property, optionIndex)}
              disabled={!isSelectable || submitting}
              className={cn(
                'group relative animate-slide-in rounded-xl border bg-white p-5 text-left shadow-sm outline-none transition-all duration-200',
                'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                property.already_onboarded
                  ? 'cursor-not-allowed border-border/60 opacity-60'
                  : isSelected
                    ? 'border-primary bg-primary/[0.03] shadow-md ring-2 ring-primary/20'
                    : submitting
                      ? 'cursor-not-allowed border-border/60 opacity-50'
                      : 'cursor-pointer border-border/60 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md'
              )}
              style={{ animationDelay: `${index * 60}ms` }}
            >
              {/* Already onboarded badge */}
              {property.already_onboarded && (
                <div className="absolute right-3 top-3">
                  <Badge variant="secondary" className="text-[10px]">
                    <CheckCircle2 className="mr-1 h-3 w-3 text-emerald-500" />
                    Already Managed
                  </Badge>
                </div>
              )}

              {/* Selection indicator — the radio dot that makes "chosen" legible at a glance. */}
              {isSelectable && (
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full border transition-all duration-200',
                    isSelected
                      ? 'border-primary bg-primary text-white'
                      : 'border-border bg-white text-transparent group-hover:border-primary/50'
                  )}
                >
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
              )}

              {/* Property icon */}
              <div
                className={cn(
                  'mb-3 flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
                  property.already_onboarded
                    ? 'bg-muted'
                    : isSelected
                      ? 'bg-primary/15'
                      : 'bg-primary/5 group-hover:bg-primary/10'
                )}
              >
                <Building2
                  className={cn(
                    'h-5 w-5',
                    property.already_onboarded ? 'text-muted-foreground' : 'text-primary'
                  )}
                />
              </div>

              {/* Property name */}
              <h3 className="text-sm font-semibold text-foreground">{property.name}</h3>

              {/* Badges row */}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 font-mono text-[11px] text-muted-foreground">
                  {property.shortname}
                </span>
                <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                  <Globe className="h-3 w-3" />
                  {property.currency}
                </span>
              </div>
            </button>
          )
        })}
      </div>

      {/* Properties with errors */}
      {errorProperties.length > 0 && (
        <div className="mt-6">
          <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Unavailable
          </p>
          <div className="space-y-2">
            {errorProperties.map((property) => (
              <div
                key={property.shortname}
                className="flex items-center gap-3 rounded-lg border border-border/40 bg-muted/30 px-4 py-3 text-sm"
              >
                <AlertCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="flex-1">
                  <span className="font-medium text-foreground">{property.name}</span>
                  <span className="ml-2 font-mono text-xs text-muted-foreground">
                    ({property.shortname})
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">{property.error}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {availableProperties.length === 0 && errorProperties.length === 0 && (
        <div className="rounded-xl border border-border/60 bg-white p-8 text-center shadow-sm">
          <Building2 className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium text-foreground">No properties found</p>
          <p className="mt-1 text-xs text-muted-foreground">
            No properties were discovered under these credentials.
          </p>
        </div>
      )}

      {/* What Connect actually commits to. Stated before the click, not after it. */}
      {selectedProperty && (
        <div
          ref={confirmRef}
          className="mt-6 animate-fade-in-up rounded-xl border border-primary/30 bg-primary/[0.04] p-5 shadow-sm"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Building2 className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">
                Ready to connect {selectedProperty.name}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Continuing adds{' '}
                <span className="font-medium text-foreground">{selectedProperty.name}</span> to{' '}
                <span className="font-medium text-foreground">{accountName || 'your account'}</span>{' '}
                now and reads its rooms and rate plans from your PMS, ready for you to review in the
                next step. No rates are published to your PMS until you go live.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error banner — beside the action that failed, not at the top of the page. */}
      {error && (
        <div className="mt-4 flex animate-fade-in-up items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Both directions, together. */}
      <div className="mt-6 flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className="rounded-lg px-1 py-2 text-sm text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:py-0"
        >
          &larr; Back to connection details
        </button>

        <Button
          type="button"
          onClick={handleConfirm}
          disabled={!selectedProperty || submitting}
          className="rounded-lg"
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Connecting...
            </>
          ) : (
            <>
              Connect property
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
